// src/components/stream/KeepaliveDialog.tsx
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { secondsLeft, type KeepalivePrompt } from "../../lib/castKeepalive";
import { retainServerClock, serverClock } from "../../lib/castSync/serverClock";
import { usePipWindow } from "../../lib/documentPip";
import { useErrorToastStore } from "../../store/errorToastStore";
import { keepaliveContextOf, useKeepaliveStore } from "../../store/keepaliveStore";

export const KEEPALIVE_TICK_MS = 250;

/**
 * The keep-alive prompt (spec 2026-10-19 §4.4). Closes only on Accept, ACCEPTED, STOP_STREAM or 0
 * (phase ALL); no close button, no Escape, no backdrop click. Over the dock, or inside the Document
 * PiP window while one is open, so a user watching there sees it.
 */
export default function KeepaliveDialog({ prompt, me }: { prompt: KeepalivePrompt; me: string }) {
  const { t } = useTranslation();
  const titleId = useId();
  const pipWindow = usePipWindow();
  const [now, setNow] = useState(() => serverClock.now());
  const sending = useKeepaliveStore((s) => s.sending[prompt.slotId] === true);
  useEffect(() => retainServerClock(), []);
  useEffect(() => {
    const id = setInterval(() => setNow(serverClock.now()), KEEPALIVE_TICK_MS);
    return () => clearInterval(id);
  }, []);
  const left = secondsLeft(prompt.deadline, now);

  // C76: ALL closes at 0 (the server stop follows). CASTER at 0 hands over to ALL, sent at that very
  // deadline: stay open at 0 s and ask GET queue once, in case that event was lost. If no ALL ever
  // comes, the CASTER prompt ends with the ALL window after it (the store's isOver, C79).
  const askedAtZero = useRef(false);
  const { slotId, promptId } = prompt;
  useEffect(() => {
    if (left !== 0) return;
    const store = useKeepaliveStore.getState();
    if (store.expireIfOver(slotId, promptId, now) || askedAtZero.current) return;
    askedAtZero.current = true;
    store.refresh(slotId, keepaliveContextOf(me));
  }, [left, now, slotId, promptId, me]);

  const accept = async () => {
    const r = await useKeepaliveStore.getState().accept(slotId);
    if (r === "retry") useErrorToastStore.getState().showError(t("cast_keepalive_retry", "Couldn't keep the stream on. Try again."));
  };

  return createPortal(
    <div data-testid="keepalive-backdrop" className="fixed inset-0 z-[1960] flex overflow-y-auto bg-black/50 px-4 py-4">
      <div role="alertdialog" aria-modal="true" aria-labelledby={titleId} data-testid="keepalive-dialog"
        className="m-auto w-full max-w-[360px] rounded-xl bg-[var(--bg-surface)] p-5 shadow-lg flex flex-col gap-3">
        <h2 id={titleId} className="text-base font-bold text-[var(--foreground)]">{t("cast_keepalive_title", "DEACTIVATE AUTOMATIC SWITCH OFF")}</h2>
        <p className="text-sm text-[var(--foreground)]">{t("cast_keepalive_body", "The stream will switch off automatically. Press Accept to keep it ON.")}</p>
        <p data-testid="keepalive-countdown" aria-live="polite" className="text-sm font-medium text-[var(--foreground-muted)]">
          {t("cast_keepalive_time_left", "Time left: {{seconds}} s", { seconds: left })}
        </p>
        <div className="flex justify-end">
          <button type="button" autoFocus data-testid="keepalive-accept" disabled={sending} onClick={() => void accept()}
            className="h-9 px-5 rounded-full text-sm bg-[var(--primary-fill)] text-white disabled:opacity-50">{t("accept", "Accept")}</button>
        </div>
      </div>
    </div>,
    pipWindow?.document.body ?? document.body,
  );
}
